import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { FaLink, FaPlus, FaTags, FaUsers, FaChartBar, FaEdit, FaTrash, FaImages, FaEye, FaToggleOn, FaToggleOff, FaCheckCircle, FaQuestionCircle, FaCreditCard, FaCog, FaPhotoVideo, FaSort, FaGripLines, FaGift, FaPaperPlane } from 'react-icons/fa'; // Добавили FaGift и FaPaperPlane
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import ContentForm from '../components/ContentForm';
import PaymentSettings from '../components/PaymentSettings';
import SalesStatistics from '../components/SalesStatistics';
import TagManagement from '../components/TagManagement';
import UserManagement from '../components/UserManagement';
import SettingsManagement from '../components/SettingsManagement';
import StoryManagement from '../components/StoryManagement';
import ReferralLinkManagement from '../components/ReferralLinkManagement'; // Добавляем импорт
import GiftManagement from '../components/GiftManagement'; // Заглушка для управления подарками
import MailingManagement from '../components/MailingManagement'; // Импортируем новый компонент
import MarketingManagement from '../components/MarketingManagement';
import { APP_VERSION } from '../version';
import './AdminPage.css';

const AdminPage = () => {
  const { isAdmin, isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('content');
  const [content, setContent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showContentForm, setShowContentForm] = useState(false);
  const [showContentSorting, setShowContentSorting] = useState(false);
  const [editingContent, setEditingContent] = useState(null);
  const [formMode, setFormMode] = useState('create'); // 'create' или 'edit'
  const [config, setConfig] = useState({ botUsername: '' });
  
  // Настройка сенсоров для drag-and-drop
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        delay: 250,
        tolerance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Обработчик drag-and-drop сортировки
  const handleDragEnd = async (event) => {
    const { active, over } = event;
    
    if (active.id !== over.id) {
      // Находим индексы элементов
      const oldIndex = content.findIndex(item => item._id === active.id);
      const newIndex = content.findIndex(item => item._id === over.id);
      
      // Создаем новый массив с измененным порядком
      const updatedContent = [...content];
      const [removedContent] = updatedContent.splice(oldIndex, 1);
      updatedContent.splice(newIndex, 0, removedContent);
      
      // Обновляем состояние
      setContent(updatedContent);
      
      // Обновляем сортировку на сервере
      try {
        const telegramId = user?.telegramId || localStorage.getItem('telegram_id');
        
        // Получаем массив ID в правильном порядке
        const contentIds = updatedContent.map(item => item._id);
        console.log('Отправляем новый порядок сортировки контента:', contentIds);
        
        // Отправляем запрос на обновление сортировки
        const response = await fetch('/api/content/update-sorting', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Telegram-ID': telegramId
          },
          body: JSON.stringify({ contentIds })
        });
        
        if (!response.ok) {
          throw new Error('Не удалось обновить сортировку');
        }
        
        const result = await response.json();
        console.log('Сортировка контента успешно обновлена:', result);
        
      } catch (error) {
        console.error('Ошибка при обновлении порядка контента:', error);
        // Восстанавливаем исходный порядок при ошибке
        fetchContent();
        alert('Ошибка при сохранении порядка сортировки');
      }
    }
  };

  // Переключение режима сортировки
  const toggleContentSorting = () => {
    // Если форма создания/редактирования открыта, закрываем её
    if (showContentForm) {
      setShowContentForm(false);
      setEditingContent(null);
    }
    
    setShowContentSorting(!showContentSorting);
  };
  
  // Перенаправляем не-админов
  useEffect(() => {
    if (!isAuthenticated || !isAdmin) {
      navigate('/');
    }
  }, [isAuthenticated, isAdmin, navigate]);
  
  // Загружаем конфигурацию
  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const response = await fetch('/api/config');
        if (response.ok) {
          const configData = await response.json();
          setConfig(configData);
        } else {
          console.error('Ошибка при загрузке конфигурации');
        }
      } catch (error) {
        console.error('Ошибка при загрузке конфигурации:', error);
      }
    };
    
    fetchConfig();
  }, []);
  
  // Функция для загрузки контента
  const fetchContent = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('auth_token');
      const telegramId = localStorage.getItem('telegram_id');
      const headers = {};
      if (token) headers['Authorization'] = `Bearer ${token}`;
      if (telegramId) headers['X-Telegram-ID'] = telegramId;

      // Всегда загружаем с sortBy=sortOrder для консистентности в админке
      const contentResponse = await fetch(`/api/content?published=all&sortBy=sortOrder`, { headers });
      
      if (!contentResponse.ok) {
        throw new Error(`HTTP ошибка: ${contentResponse.status}`);
      }
      
      const contentData = await contentResponse.json();
      let parsedContent = [];
      
      if (Array.isArray(contentData)) {
        parsedContent = contentData;
      } else if (contentData.content) {
        parsedContent = contentData.content;
      } else {
        console.error('Неизвестный формат данных:', contentData);
      }
      setContent(parsedContent);
    } catch (error) {
      console.error('Ошибка при загрузке контента:', error);
      // Оставляем старый контент при ошибке
    } finally {
      setLoading(false);
    }
  }, []); // Пустой массив зависимостей, т.к. setContent и setLoading стабильны, а URL не зависит от пропсов/стейта этого компонента динамически

  // Загружаем данные только при активации вкладки контента или смене пользователя
  useEffect(() => {
    if (activeTab === 'content') {
      fetchContent();
    }
  }, [activeTab, user, fetchContent]); // fetchContent теперь стабилен
  
  // Обработчики действий админ-панели
  const handleAddContent = () => {
    setEditingContent(null);
    setFormMode('create');
    setShowContentForm(true);
  };
  
  const handleEditContent = (id) => {
    const contentToEdit = content.find(item => (item._id || item.id) === id);
    if (contentToEdit) {
      setEditingContent(contentToEdit);
      setFormMode('edit');
      setShowContentForm(true);
    }
  };
  
  const handleCancelForm = () => {
    setShowContentForm(false);
    setEditingContent(null);
  };
  
  const handleSaveContent = async (contentData) => {
    try {
      const telegramId = user?.telegramId || localStorage.getItem('telegram_id');
      const headers = {
        'Content-Type': 'application/json',
        'X-Telegram-ID': telegramId
      };
      
      let url = '/api/content';
      let method = 'POST';
      
      // Если это редактирование, меняем метод и добавляем ID
      if (formMode === 'edit' && editingContent) {
        const contentId = editingContent._id || editingContent.id;
        url = `/api/content/${contentId}`;
        method = 'PUT';
      }
      
      const response = await fetch(url, {
        method,
        headers,
        body: JSON.stringify(contentData)
      });
      
      if (!response.ok) {
        throw new Error(`Ошибка при ${formMode === 'create' ? 'создании' : 'обновлении'} контента`);
      }
      
      // Закрываем форму и очищаем состояние
      setShowContentForm(false);
      setEditingContent(null);
      
      // Обновляем список контента
      fetchContent();
      
      // Оповещаем пользователя
      alert(`Контент успешно ${formMode === 'create' ? 'создан' : 'обновлен'}`);
    } catch (error) {
      console.error('Ошибка при сохранении контента:', error);
      alert(`Ошибка при сохранении: ${error.message}`);
    }
  };
  
  const handleDeleteContent = (id) => {
    const confirmDelete = window.confirm('Вы уверены, что хотите удалить этот контент?');
    if (confirmDelete) {
      const deleteContent = async () => {
        try {
          const telegramId = user?.telegramId || localStorage.getItem('telegram_id');
          const response = await fetch(`/api/content/${id}`, {
            method: 'DELETE',
            headers: {
              'X-Telegram-ID': telegramId
            }
          });
          
          if (!response.ok) {
            throw new Error('Ошибка при удалении контента');
          }
          
          // Обновляем список контента
          fetchContent();
          
          alert('Контент успешно удален');
        } catch (error) {
          console.error('Ошибка при удалении контента:', error);
          alert(error.message);
        }
      };
      
      deleteContent();
    }
  };
  
  // Функции для управления материалами
  const handleAddMaterials = (id) => {
    // Проверяем загружена ли конфигурация
    if (!config.botUsername) {
      alert('Ошибка: не удалось получить имя бота. Обновите страницу или обратитесь к администратору.');
      return;
    }

    // Открываем Telegram Bot с параметром для добавления материалов
    const tgApp = window.Telegram?.WebApp;
    if (tgApp) {
      tgApp.openTelegramLink(`https://t.me/${config.botUsername}?start=materials_${id}`);
      
      // Закрываем WebApp через короткую задержку после открытия бота
      setTimeout(() => {
        tgApp.close();
      }, 100);
    } else {
      window.open(`https://t.me/${config.botUsername}?start=materials_${id}`, '_blank');
    }
  };
  
  const handleAddPreview = (id) => {
    // Проверяем, загружена ли конфигурация
    if (!config.botUsername) {
      alert('Ошибка: не удалось получить имя бота. Обновите страницу или обратитесь к администратору.');
      return;
    }
    
    // Открываем Telegram Bot с параметром для добавления превью
    const tgApp = window.Telegram?.WebApp;
    if (tgApp) {
      tgApp.openTelegramLink(`https://t.me/${config.botUsername}?start=preview_${id}`);
      
      // Закрываем WebApp через короткую задержку после открытия бота
      setTimeout(() => {
        tgApp.close();
      }, 100);
    } else {
      window.open(`https://t.me/${config.botUsername}?start=preview_${id}`, '_blank');
    }
  };
  
  const handleTogglePublish = async (id, isPublished) => {
    try {
      const telegramId = user?.telegramId || localStorage.getItem('telegram_id');
      
      const headers = {
        'Content-Type': 'application/json',
        'X-Telegram-ID': telegramId
      };
      
      const response = await fetch(`/api/content/${id}/toggle-publish`, {
        method: 'POST',
        headers
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Ошибка при изменении статуса публикации');
      }
      
      // Обновляем список контента
      fetchContent();
      
      alert(`Контент ${!isPublished ? 'опубликован' : 'снят с публикации'}`);
    } catch (error) {
      console.error('Ошибка при изменении статуса публикации:', error);
      alert(`Ошибка при изменении статуса публикации: ${error.message}`);
    }
  };
  
  // Форматирование даты
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };
  
  if (!isAuthenticated || !isAdmin) {
    return null;
  }
  
  return (
      <div className="admin-page content-with-navbar">
      <div className="tg-page-header"></div>
      <div className="admin-header">
        <h1 className="admin-title">Административная панель</h1>
        <div className="admin-version" style={{ fontSize: '14px', color: '#6c757d', marginTop: '5px' }}>
          Версия: {APP_VERSION}
        </div>
      </div>
      
      <div className="admin-tabs">
        <button 
          className={`tab-button ${activeTab === 'content' ? 'active' : ''}`}
          onClick={() => setActiveTab('content')}
        >
          Посты
        </button>
        <button 
          className={`tab-button ${activeTab === 'stories' ? 'active' : ''}`}
          onClick={() => setActiveTab('stories')}
        >
          <FaPhotoVideo /> Истории
        </button>
        <button 
          className={`tab-button ${activeTab === 'tags' ? 'active' : ''}`}
          onClick={() => setActiveTab('tags')}
        >
          <FaTags /> Теги
        </button>
        <button 
          className={`tab-button ${activeTab === 'users' ? 'active' : ''}`}
          onClick={() => setActiveTab('users')}
        >
          <FaUsers /> Клиенты
        </button>
        <button 
          className={`tab-button ${activeTab === 'payments' ? 'active' : ''}`}
          onClick={() => setActiveTab('payments')}
        >
          <FaCreditCard /> Платежка
        </button>
        <button 
          className={`tab-button ${activeTab === 'stats' ? 'active' : ''}`}
          onClick={() => setActiveTab('stats')}
        >
          <FaChartBar /> Продажи
        </button>
        <button 
          className={`tab-button ${activeTab === 'settings' ? 'active' : ''}`}
          onClick={() => setActiveTab('settings')}
        >
          <FaCog /> Настройки
        </button>
        <button 
          className={`tab-button ${activeTab === 'referrals' ? 'active' : ''}`}
          onClick={() => setActiveTab('referrals')}
        >
          <FaLink /> Urls
        </button>
        <button 
          className={`tab-button ${activeTab === 'gifts' ? 'active' : ''}`}
          onClick={() => setActiveTab('gifts')}
        >
          <FaGift /> Подарки
        </button>
        <button 
          className={`tab-button ${activeTab === 'mailings' ? 'active' : ''}`}
          onClick={() => setActiveTab('mailings')}
        >
          <FaPaperPlane /> Рассылки
        </button>
        <button 
          className={`tab-button ${activeTab === 'marketing' ? 'active' : ''}`}
          onClick={() => setActiveTab('marketing')}
        >
          <FaTags /> Маркетинг
        </button>
      </div>
      
      {/* Контент-менеджмент */}
      {activeTab === 'content' && (() => {
        return (
          <div className="tab-content" key={`content-tab-${Date.now()}`}>
            {showContentForm ? (
              <ContentForm 
                content={editingContent} 
                onSave={handleSaveContent}
                onCancel={handleCancelForm}
              />
            ) : (
              <>
                <div className="content-header">
                  <button className="add-button wide-button" onClick={handleAddContent}>
                    <FaPlus /> Создать
                  </button>
                  <button className="add-button wide-button" onClick={toggleContentSorting}>
                    <FaSort /> {showContentSorting ? 'Закрыть' : 'Сортировка'}
                  </button>
                </div>

                {/* Режим сортировки контента */}
                {showContentSorting && (
                  <div className="content-sort-container">
                    <h3>Упорядочите посты перетаскиванием</h3>
                    {loading ? (
                      <div className="loading-state">
                        <div className="loading-spinner"></div>
                        <p>Загрузка контента...</p>
                      </div>
                    ) : content.length === 0 ? (
                      <div className="empty-state">
                        <p>Нет созданного контента</p>
                      </div>
                    ) : (
                      <DndContext 
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragEnd={handleDragEnd}
                      >
                        <SortableContext
                          items={content.map(item => item._id)}
                          strategy={verticalListSortingStrategy}
                        >
                          <div className="sortable-content-list">
                            {content.map(item => (
                              <SortableContentItem 
                                key={item._id} 
                                contentItem={item}
                              />
                            ))}
                          </div>
                        </SortableContext>
                      </DndContext>
                    )}
                  </div>
                )}
                
                {/* Обычный список контента (не в режиме сортировки) */}
                {!showContentSorting && (
                  <>
                    {loading ? (
                      <div className="loading-state">
                        <div className="loading-spinner"></div>
                        <p>Загрузка...</p>
                      </div>
                    ) : (
                      <div className="admin-table-wrapper">
                        <div className="content-list">
                          {content.map(item => {
                            // Адаптируем поля к MongoDB или SQLite структуре
                            const itemId = item._id || item.id;
                            const isFree = item.isFree || item.is_free;
                            const isPublished = item.isPublished || false;
                            const viewCount = item.viewCount || item.view_count || 0;
                            const purchaseCount = item.purchaseCount || item.purchase_count || 0;
                            const createdAt = item.createdAt || item.created_at;
                            
                            return (
                              <div key={itemId} className="content-item">
                                <div className="content-item-header">
                                  <div className="content-title">
                                    <h3>{item.title}</h3>
                                    <div className="content-meta">
                                      {isFree ? 'Бесплатно' : `${item.price} ₽`} • 
                                      Просмотры: {viewCount} • 
                                      Покупки: {purchaseCount} • 
                                      Создан: {formatDate(createdAt)}
                                    </div>
                                  </div>
                                  <div className="content-status">
                                    {isPublished ? (
                                      <span className="status-icon published" title="Опубликован">
                                        <FaCheckCircle />
                                      </span>
                                    ) : (
                                      <span className="status-icon draft" title="Черновик">
                                        <FaQuestionCircle />
                                      </span>
                                    )}
                                  </div>
                                </div>
                                <div className="content-actions">
                                  <div className="button-row">
                                    <button 
                                      className="admin-button" 
                                      onClick={() => handleEditContent(itemId)}
                                    >
                                      <FaEdit /> Редактировать
                                    </button>
                                    <button 
                                      className="admin-button" 
                                      onClick={() => handleAddMaterials(itemId)}
                                    >
                                      <FaImages /> Материалы ({item.materialsCount || 0})
                                    </button>
                                  </div>
                                  
                                  <div className="button-row">
                                    <button 
                                      className="admin-button" 
                                      onClick={() => handleAddPreview(itemId)}
                                    >
                                      <FaEye /> Превью ({item.previewCount || 0})
                                    </button>
                                    <button 
                                      className="admin-button"
                                      onClick={() => handleTogglePublish(itemId, isPublished)}
                                    >
                                      {isPublished ? <FaToggleOff /> : <FaToggleOn />} 
                                      {isPublished ? "Снять с публикации" : "Опубликовать"}
                                    </button>
                                  </div>
                                  
                                  <div className="button-row">
                                    <button 
                                      className="admin-button admin-button-delete" 
                                      onClick={() => handleDeleteContent(itemId)}
                                    >
                                      <FaTrash /> Удалить
                                    </button>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </>
            )}
          </div>
        );
      })()}
      
      {/* Управление тегами */}
      {activeTab === 'tags' && (() => {
        return (
          <div className="tab-content">
            <TagManagement key={`tag-management-${Date.now()}`} />
          </div>
        );
      })()}
      
      {/* Управление историями */}
      {activeTab === 'stories' && (() => {
        return (
          <div className="tab-content">
            <StoryManagement key={`story-management-tab-${Date.now()}`} />
          </div>
        );
      })()}
      
      {/* Управление пользователями */}
      {activeTab === 'users' && (() => {
        return (
          <div className="tab-content">
            <UserManagement key={`user-management-tab-${Date.now()}`} />
          </div>
        );
      })()}
      
      {/* Настройки платежей */}
      {activeTab === 'payments' && (() => {
        return (
          <div className="tab-content">
            <PaymentSettings key={`payment-settings-${Date.now()}`} />
          </div>
        );
      })()}
      
      {/* Статистика */}
      {activeTab === 'stats' && (() => {
        return (
          <div className="tab-content">
            <SalesStatistics key={`sales-statistics-${Date.now()}`} />
          </div>
        );
      })()}
      
      {/* Настройки */}
      {activeTab === 'settings' && (() => {
        return (
          <div className="tab-content">
            <SettingsManagement key={`settings-management-${Date.now()}`} />
          </div>
        );
      })()}

      {/* Управление реферальными ссылками (новый раздел) */}
      {activeTab === 'referrals' && (() => {
        return (
          <div className="tab-content">
            <ReferralLinkManagement 
              key={`referral-link-management-${Date.now()}`} 
              botUsername={config.botUsername} 
            />
          </div>
        );
      })()}

      {/* Управление подарками (новый раздел) */}
      {activeTab === 'gifts' && (() => {
        return (
          <div className="tab-content">
            <GiftManagement key={`gift-management-${Date.now()}`} />
          </div>
        );
      })()}

      {/* Управление рассылками (новый раздел) */}
      {activeTab === 'mailings' && (() => {
        return (
          <div className="tab-content" key={`mailings-tab-${Date.now()}`}>
            <MailingManagement />
          </div>
        );
      })()}

      {/* Управление маркетингом (новый раздел) */}
      {activeTab === 'marketing' && (() => {
        return (
          <div className="tab-content">
            <MarketingManagement key={`marketing-management-${Date.now()}`} />
          </div>
        );
      })()}
    </div>
  );
};

// Компонент для сортируемого элемента контента
const SortableContentItem = ({ contentItem }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id: contentItem._id });
  
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : 1,
    position: 'relative'
  };

  return (
    <div ref={setNodeRef} style={style} className="sortable-content-item">
      <div className="content-item-content">
        <div 
          {...attributes} 
          {...listeners} 
          className="drag-handle" 
          style={{ 
            cursor: 'grab', 
            padding: '10px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            touchAction: 'none'
          }}
        >
          <FaGripLines size={20} />
        </div>
        
        {contentItem.imageUrl && (
          <div className="content-thumbnail" style={{
            width: '60px',
            height: '60px',
            borderRadius: '8px',
            overflow: 'hidden',
            flexShrink: 0,
            marginRight: '12px'
          }}>
            <img 
              src={contentItem.imageUrl} 
              alt={contentItem.title}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover'
              }}
            />
          </div>
        )}
        
        <div className="content-info" style={{ flex: 1 }}>
          <strong>{contentItem.title || 'Без названия'}</strong>
          <div className="content-status-info" style={{
            display: 'flex',
            gap: '8px',
            flexWrap: 'wrap',
            marginTop: '6px'
          }}>
            {contentItem.isPinned && (
              <span style={{
                fontSize: '0.75rem',
                padding: '2px 6px',
                borderRadius: '4px',
                fontWeight: '500',
                background: '#ffc107',
                color: '#856404'
              }}>Закреплен</span>
            )}
            {!contentItem.isPublished && (
              <span style={{
                fontSize: '0.75rem',
                padding: '2px 6px',
                borderRadius: '4px',
                fontWeight: '500',
                background: '#dc3545',
                color: 'white'
              }}>Не опубликован</span>
            )}
            <span style={{
              fontSize: '0.75rem',
              padding: '2px 6px',
              borderRadius: '4px',
              fontWeight: '500',
              background: '#28a745',
              color: 'white'
            }}>
              {contentItem.isFree ? 'Бесплатно' : `${contentItem.price}₽`}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminPage;
